"use client";

/**
 * Client body for the GitLab integration page. Lives apart from `page.tsx`
 * (which stays a server component for `metadata`) so it can call `useT()` and
 * follow the visitor's locale, exactly like the homepage. All copy comes from
 * the `intgGitlab` namespace; structure (icons, hrefs, capability status)
 * stays here.
 */

import {
  GitBranch,
  GitMerge,
  FileDiff,
  ShieldCheck,
  Lock,
  History,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  FeatureGrid,
  CapabilityGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function GitlabContent() {
  const t = useT();
  const k = (s: string) => `intgGitlab.${s}`;

  const features = [
    { key: "repoContext", icon: <GitBranch size={18} /> },
    { key: "proposedMr", icon: <GitMerge size={18} /> },
    { key: "diff", icon: <FileDiff size={18} /> },
    { key: "humanMerge", icon: <ShieldCheck size={18} /> },
    { key: "scoped", icon: <Lock size={18} /> },
    { key: "audit", icon: <History size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const capabilities = [
    { key: "readContext", status: "supported" as const },
    { key: "openMr", status: "conditional" as const },
    { key: "diffEvidence", status: "supported" as const },
    { key: "mergeAuto", status: "unavailable" as const },
    { key: "singleGreen", status: "unavailable" as const },
  ].map((c) => ({
    name: t(k(`capabilities.${c.key}.name`)),
    status: c.status,
    body: t(k(`capabilities.${c.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5", "q6"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{
          href: "/use-cases/support-to-engineering-remediation",
          label: t(k("hero.secondaryCta")),
        }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section>
        <SectionHeader eyebrow={t(k("what.eyebrow"))} title={t(k("what.title"))} />
        <FeatureGrid features={features} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("capabilities.eyebrow"))}
          title={t(k("capabilities.title"))}
          description={t(k("capabilities.description"))}
        />
        <CapabilityGrid
          capabilities={capabilities}
          statusLabels={{
            supported: t("mk.cap.supported"),
            conditional: t("mk.cap.conditional"),
            unavailable: t("mk.cap.unavailable"),
          }}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("governed.eyebrow"))} title={t(k("governed.title"))} />
        <SecurityNote icon={<History size={18} />} title={t(k("governed.noteTitle"))}>
          {t(k("governed.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/support-to-engineering-remediation",
              label: t(k("related.supportEng.label")),
              description: t(k("related.supportEng.description")),
            },
            {
              href: "/integrations/github",
              label: t(k("related.github.label")),
              description: t(k("related.github.description")),
            },
            {
              href: "/use-cases/ai-deployment-governance",
              label: t(k("related.deployGov.label")),
              description: t(k("related.deployGov.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
